import { Model, type ModelProps } from '@/infra/model'
import { Id } from '@/infra/id'
import { action, computed, observable } from 'mobx'
import { unixEpoch } from '@/utils/date-util'
import { collection } from '@/infra/collection'
import { once } from 'lodash-es'

export type JobProgressionProps = ModelProps<JobProgression> & {
  recordedSteps: ProgressionStepProps[]
}

/**
 * Progression information for a job where progression is enabled.
 */
export class JobProgression extends Model<JobProgression> {
  /**
   * ID of the job that the progression belongs to.
   */
  jobId = Id.none

  /**
   * The progression flow to use.
   */
  @observable accessor flow: ProgressionFlow = 'Retail'

  /**
   * The time an extension was filed for the job, if at all.
   *
   * Unlike {@link JobProgression.recordedSteps}, this survives the job moving
   * on to a different progression flow once the extension completes.
   */
  @observable accessor extensionFiledAt: Date | null = null

  /**
   * Recorded steps in the progression.
   */
  readonly recordedSteps = collection({
    create: () => new ProgressionStep(),
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
  })

  /**
   * Returns the current status.
   */
  @computed
  get current(): ProgressionStepStatus {
    if (this.recordedSteps.length === 0) {
      return 'NotStarted'
    }

    return this.recordedSteps.items[this.recordedSteps.length - 1].step
  }

  /**
   * @inheritDoc
   */
  @action.bound
  override set({
    recordedSteps,
    ...props
  }: Partial<JobProgressionProps>): this {
    this.recordedSteps.reset(recordedSteps)
    return super.set(props)
  }

  /**
   * Empty instance.
   */
  static get none() {
    return lazyNone()
  }
}

const lazyNone = once(() => new JobProgression())

export type ProgressionStepProps = ModelProps<ProgressionStep>

/**
 * A step in the progression.
 */
export class ProgressionStep extends Model<ProgressionStep> {
  /**
   * ID of the step instance.
   */
  id = Id.none

  /**
   * The step value.
   */
  @observable accessor step: ProgressionStepStatus = 'NotStarted'

  /**
   * The step response, if any.
   */
  @observable accessor response: ProgressionStepResponse | null = null

  /**
   * The time at which the step was created.
   */
  @observable accessor createdAt = unixEpoch()
}

/**
 * Progression flows.
 */
export type ProgressionFlow =
  | 'Retail'
  | 'Outsourcing'
  | 'Essential'
  | 'Preparation'
  | 'Review'
  | 'PersonalExtension'
  | 'BusinessExtension'
  | 'OutsourcingExtension'

/**
 * Progression step status.
 */
export type ProgressionStepStatus =
  | 'JobStarted'
  | 'OpenItems'
  | 'DraftInReview'
  | 'DraftRejected'
  | 'DraftApproved'
  | 'NotStarted'
  | 'AuthorizationRequested'
  | 'AuthorizationSigned'
  | 'ReturnFiled'
  | 'DeliverableCompleted'
  | 'PreparationCompleted'
  | 'ReviewCompleted'
  | 'ExtensionInProgress'
  | 'ExtensionConfirmed'
  | 'ExtensionAccepted'
  | 'ExtensionRejected'
  | 'ProgressionWaitingOnClient'
  | 'ReadyToFile'

/**
 * A response to a progression step.
 */
export interface ProgressionStepResponse {
  /**
   * The step that was set to be the next one.
   */
  readonly step: ProgressionStepStatus

  /**
   * Time at which the response was captured.
   */
  readonly respondedAt: Date
}
